import React, { useMemo, useState } from "react";
import {
  CalendarDay,
  ClassNames,
  CustomComponents,
  DayPicker,
  DropdownOption,
  Modifiers,
  useDayPicker
} from "react-day-picker";
import { cn } from "@/utils/utils";
import "react-day-picker/dist/style.css";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp
} from "lucide-react";
import Text from "./text";
import { DateRange } from "@/components/controls/DateRangePicker";
import {
  format,
  addDays,
  endOfWeek,
  addWeeks,
  addMonths,
  isSameDay,
  isBefore,
  isAfter,
  startOfDay
} from "date-fns";
import { isWithinDays } from "@/utils/datetime";
import IconContainer from "@/components/ui/icon-container";
import { Check } from "@dreamhub/designsystem/icons";

/** Already-translated labels for the presets sidebar. */
export interface DatePresetLabels {
  today: string;
  tomorrow: string;
  endOfWeek: string;
  nextWeek: string;
  inTwoWeeks: string;
  inAMonth: string;
}

// Labels are required whenever the sidebar can show.
type PresetProps =
  | { showPresets?: false; presetLabels?: never }
  | { showPresets: boolean; presetLabels: DatePresetLabels };

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  onYearSelect?: (range: DateRange) => void;
  availableFrom?: Date;
  availableTo?: Date;
  onPresetSelect?: (date: Date) => void;
} & PresetProps;

const icons = {
  left: ChevronLeft,
  right: ChevronRight,
  down: ChevronDown,
  up: ChevronUp
};

interface CustomYearsDropdownProps {
  startYear: number;
  endYear: number;
  onYearSelect?: (range: DateRange) => void;
}

function CustomYearsDropdown({
  startYear,
  endYear,
  onYearSelect
}: CustomYearsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { months, goToMonth } = useDayPicker();
  const currentMonth = months[0].date;
  const currentYear = currentMonth.getFullYear();

  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const handleYearClick = (year: number) => {
    const newDate = new Date(new Date(currentMonth).setFullYear(year));
    goToMonth(newDate);
    if (onYearSelect) {
      onYearSelect({ toDate: newDate, fromDate: newDate });
    }
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };
  return (
    <div className="flex flex-col gap-5 bg-surface-elevation2">
      <button onClick={handleToggle} className="flex gap-1 items-center">
        <Text variant="label">{currentYear}</Text>
        <span className="size-5 flex items-center justify-center text-secondary">
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </span>
      </button>
      <div
        className={cn(
          "z-20 max-h-[5rem] w-[18rem] overflow-auto",
          isOpen ? "grid grid-cols-4" : "hidden"
        )}
      >
        {years.map((year) => (
          <button
            key={year}
            onClick={() => handleYearClick(year)}
            className={cn(
              "rounded-lg w-fit px-2 hover:bg-surface-hover",
              year === currentYear && "!bg-buttons-cta-primary"
            )}
          >
            <Text
              variant="body"
              className={year === currentYear ? "text-on-primary" : ""}
            >
              {year}
            </Text>
          </button>
        ))}
      </div>
    </div>
  );
}

interface PresetOption {
  label: string;
  date: Date;
  displayDate: string;
}

interface DatePresetsProps {
  labels: DatePresetLabels;
  onSelect: (date: Date) => void;
  selectedDate?: Date | null;
  selectedRange?: { from?: Date; to?: Date } | null;
  mode?: "single" | "range" | "multiple";
}

function DatePresets({
  labels,
  onSelect,
  selectedDate,
  selectedRange,
  mode = "single"
}: DatePresetsProps) {
  const presets: PresetOption[] = useMemo(() => {
    const today = new Date();
    const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 });
    const nextWeekDate = addWeeks(today, 1);
    const twoWeeksDate = addWeeks(today, 2);
    const monthDate = addMonths(today, 1);

    const formatDayDisplay = (date: Date, baseDate: Date = today) => {
      if (isWithinDays(date, baseDate, 7)) {
        return format(date, "EEE"); // Short day name like "Sun", "Mon", etc.
      }
      return format(date, "d MMM"); // Month and day like "May 12"
    };

    const options = [
      {
        label: labels.today,
        date: today,
        displayDate: ""
      },
      {
        label: labels.tomorrow,
        date: addDays(today, 1),
        displayDate: formatDayDisplay(addDays(today, 1))
      },
      {
        label: labels.endOfWeek,
        date: endOfCurrentWeek,
        displayDate: formatDayDisplay(endOfCurrentWeek)
      },
      {
        label: labels.nextWeek,
        date: nextWeekDate,
        displayDate: formatDayDisplay(nextWeekDate)
      },
      {
        label: labels.inTwoWeeks,
        date: twoWeeksDate,
        displayDate: formatDayDisplay(twoWeeksDate)
      },
      {
        label: labels.inAMonth,
        date: monthDate,
        displayDate: formatDayDisplay(monthDate)
      }
    ];

    return options;
  }, [labels]);

  const isPresetSelected = (presetDate: Date): boolean => {
    if (mode === "single" && selectedDate) {
      return isSameDay(presetDate, selectedDate);
    } else if (mode === "range" && selectedRange) {
      // For range mode, check if the preset date matches either the start or end date
      const { from, to } = selectedRange;
      if (from && to) {
        return isSameDay(presetDate, from) || isSameDay(presetDate, to);
      } else if (from) {
        return isSameDay(presetDate, from);
      }
    } else if (mode === "multiple" && Array.isArray(selectedDate)) {
      // For multiple selection mode
      return selectedDate.some((date) => isSameDay(date, presetDate));
    }
    return false;
  };

  return (
    <div className="min-w-48 mt-14">
      <div className="w-full border-t mt-2 border-border-border2" />
      <div className="p-3 flex flex-col gap-3">
        {presets.map((preset) => {
          const isSelected = isPresetSelected(preset.date);
          return (
            <button
              key={preset.label}
              onClick={() => onSelect(preset.date)}
              className={cn(
                "flex items-center justify-between w-full py-2 px-3 rounded-md hover:bg-surface-hover text-left",
                isSelected && "cursor-not-allowed hover:!bg-transparent"
              )}
            >
              <Text
                variant="body"
                className={isSelected ? "font-semibold" : ""}
              >
                {preset.label}
              </Text>
              {isSelected ? (
                <IconContainer size="5">
                  <Check />
                </IconContainer>
              ) : (
                <Text variant="smBodyTertiary">{preset.displayDate}</Text>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Calendar({
  className,
  showOutsideDays = true,
  onYearSelect,
  availableFrom,
  availableTo,
  showPresets = false,
  presetLabels,
  onPresetSelect,
  ...props
}: CalendarProps): JSX.Element {
  const today = new Date();
  const currentYear = today.getFullYear();
  const isMonday = (date: Date) => date.getDay() === 1;
  const isSunday = (date: Date) => date.getDay() === 0;

  const getSelectedDate = () => {
    if ("selected" in props) {
      return props.selected;
    }
    return null;
  };

  const selectedValue = getSelectedDate();
  const mode =
    "mode" in props
      ? (props.mode as "single" | "range" | "multiple")
      : "single";

  const handlePresetSelect = (date: Date) => {
    if (onPresetSelect) {
      onPresetSelect(date);
    } else if ("onSelect" in props) {
      const { onSelect, mode, selected } = props as {
        onSelect: (value: Date | Date[] | { from?: Date; to?: Date }) => void;
        mode?: "single" | "range" | "multiple";
        selected?: unknown;
      };
      if (mode === "single") {
        onSelect(date);
      } else if (mode === "multiple" && Array.isArray(selected)) {
        onSelect([...(selected || []), date]);
      } else if (mode === "range") {
        const rangeSelected = selected as
          | { from?: Date; to?: Date }
          | undefined;
        if (!rangeSelected?.from) {
          onSelect({ from: date });
        } else if (rangeSelected.from && !rangeSelected.to) {
          onSelect({ from: rangeSelected.from, to: date });
        } else {
          onSelect({ from: date });
        }
      } else {
        // Default single mode behavior
        onSelect(date);
      }
    }
  };

  const isDateDisabled = useMemo(() => {
    return (date: Date) => {
      const normalizedDate = startOfDay(date);
      if (availableFrom && availableTo) {
        const normalizedFrom = startOfDay(availableFrom);
        const normalizedTo = startOfDay(availableTo);
        return (
          isBefore(normalizedDate, normalizedFrom) ||
          isAfter(normalizedDate, normalizedTo)
        );
      } else if (availableFrom) {
        const normalizedFrom = startOfDay(availableFrom);
        return isBefore(normalizedDate, normalizedFrom);
      } else if (availableTo) {
        const normalizedTo = startOfDay(availableTo);
        return isAfter(normalizedDate, normalizedTo);
      }
      return false;
    };
  }, [availableFrom, availableTo]);

  const DayButton = useMemo(
    () =>
      (
        props: {
          day: CalendarDay;
          modifiers: Modifiers;
          className?: string;
        } & React.ButtonHTMLAttributes<HTMLButtonElement>
      ) => {
        const { day, modifiers, className, ...buttonProps } = props;
        const date = day.date;
        const isRangeStart = modifiers.range_start;
        const isRangeEnd = modifiers.range_end;
        const isRangeMiddle = modifiers.range_middle;
        const isToday = modifiers.today;
        const isSelected = modifiers.selected;
        const isDisabled = modifiers.disabled;

        // Check if this is a single selection (not part of a range)
        const isSingleSelection =
          isSelected && !isRangeStart && !isRangeEnd && !isRangeMiddle;

        const isInRange =
          isRangeStart || isRangeEnd || isRangeMiddle || isSelected;

        return (
          <div className={cn("relative", showPresets ? "size-11" : "size-8")}>
            {(isRangeStart || isRangeMiddle || isRangeEnd) && (
              <div
                className={cn(
                  "absolute inset-0 z-0",
                  isRangeStart && "rounded-l-full",
                  isRangeEnd && "rounded-r-full",
                  isRangeMiddle && "rounded-none"
                )}
                style={{
                  backgroundColor: "var(--surface-calendar-range)"
                }}
              />
            )}
            <button
              {...buttonProps}
              type="button"
              className={cn(
                className,
                showPresets ? "size-11" : "size-8",
                "relative z-10",
                isRangeStart || isRangeEnd || isSingleSelection
                  ? "!bg-buttons-cta-primary !text-on-primary rounded-full"
                  : "bg-transparent rounded-full",
                isToday && !isInRange && "border border-primary text-primary",
                isToday && isRangeMiddle && "text-primary",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <span className="relative z-20">{date.getDate()}</span>
            </button>
          </div>
        );
      },
    [showPresets]
  );

  const Chevron = useMemo(
    () =>
      (props: {
        className?: string | undefined;
        size?: number | undefined;
        disabled?: boolean | undefined;
        orientation?: "up" | "down" | "left" | "right" | undefined;
      }) => {
        const Component = icons[props.orientation || "down"];
        return <Component {...props} className="text-secondary" />;
      },
    []
  );

  const YearsDropdown = useMemo(
    () =>
      (
        props: {
          components: CustomComponents;
          classNames: ClassNames;
          options?: DropdownOption[] | undefined;
        } & Omit<
          React.DetailedHTMLProps<
            React.SelectHTMLAttributes<HTMLSelectElement>,
            HTMLSelectElement
          >,
          "children"
        >
      ) => (
        <CustomYearsDropdown
          {...props}
          startYear={currentYear - 10}
          endYear={currentYear + 10}
          onYearSelect={onYearSelect}
        />
      ),
    [onYearSelect, currentYear]
  );

  const calendarComponent = (
    <DayPicker
      captionLayout="dropdown-years"
      showOutsideDays={showOutsideDays}
      fixedWeeks
      startMonth={new Date(today.getFullYear() - 10, today.getMonth())}
      endMonth={new Date(today.getFullYear() + 10, today.getMonth())}
      defaultMonth={
        selectedValue ? new Date(selectedValue as string | Date) : today
      }
      className={cn("px-0", showPresets && "mt-10 pt-3", className)}
      formatters={{
        formatWeekdayName: (weekday) => weekday.toString().charAt(0)
      }}
      weekStartsOn={1}
      disabled={isDateDisabled}
      classNames={{
        month: "text-base cursor-auto",
        month_caption: showPresets
          ? "absolute -left-[180px] -top-[40px] z-50"
          : "",
        nav: cn("absolute right-4 top-4", showPresets && "right-0 -top-[40px]"),
        dropdowns: cn("flex flex-col gap-1 p-4", showPresets && "p-0 pl-3"),
        caption_label: "text-base font-semibold flex items-center",
        months_dropdown: "cursor-default",
        years_dropdown: "cursor-pointer",
        outside: "text-secondary",
        month_grid: cn(
          "mt-3 border-t border-border-border2",
          showPresets && "border-l"
        ),
        weekdays: cn(
          "flex w-full px-4 pt-1 justify-around text-secondary",
          showPresets && "pt-0"
        ),
        selected: "",
        range_start: "",
        range_end: "",
        range_middle: "",
        week: "flex py-1 px-4",
        day: showPresets ? "size-11" : "size-8"
      }}
      modifiers={{ monday: isMonday, sunday: isSunday }}
      modifiersStyles={{
        monday: {
          borderTopLeftRadius: "100%",
          borderBottomLeftRadius: "100%"
        },
        sunday: {
          borderTopRightRadius: "100%",
          borderBottomRightRadius: "100%"
        }
      }}
      {...props}
      components={{
        Chevron,
        DayButton,
        YearsDropdown
      }}
    />
  );

  if (!showPresets || !presetLabels) {
    return calendarComponent;
  }

  return (
    <div className="flex">
      <DatePresets
        labels={presetLabels}
        onSelect={handlePresetSelect}
        selectedDate={mode === "single" ? (selectedValue as Date) : null}
        selectedRange={
          mode === "range"
            ? (selectedValue as { from?: Date; to?: Date })
            : null
        }
        mode={mode}
      />
      {calendarComponent}
    </div>
  );
}

export default Calendar;
